import { styled } from '@linaria/react';
import { Modal } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Epic } from '../../../api/tasks-client/task-client.type';
import { useDeepCompareEffect } from '../../../hooks/useDeepCompareEffect';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useStrategyStore } from '../../../store/strategy-store/strategy-store';
import { setSelectedInitiative } from '../../../store/strategy-store/strategy-store.actions';
import { skyBase } from '../../../styles/design-tokens';
import { Text } from '../../../ui-library/typography/typography';
import { useEpicsQuery } from '../strategy.hooks';
import { StepOne } from './step-one';
import { StepTwo } from './step-two';
import {
  useInitialEpics,
  useInitialInitiatives,
  useInitialOutcomes,
  useInitialValues,
} from './strategy-entity-modal.hooks';
import type { StrategyEntityModalProps } from './strategy-entity-modal.type';

export function StrategyEntityModal({
  mode,
  entityType,
  entity,
  opened,
  onClose,
  onSubmit,
  isSubmitting,
  onDelete,
  isDeleting,
}: StrategyEntityModalProps) {
  const i18n = useTranslation();
  useEpicsQuery({ enabled: opened && entityType === 'initiative' });

  const portfolioId = useGlobalStore((state) => state.portfolio?.id);
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const selectedInitiative = useStrategyStore((state) => state.selectedInitiative);

  const initialValues = useInitialValues(entity, entityType);
  const initialEpics = useInitialEpics(entity, entityType);
  const initialInitiatives = useInitialInitiatives(entity, entityType);
  const initialOutcomes = useInitialOutcomes(entity, entityType);

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<Partial<any>>({});
  const [selectedEpics, setSelectedEpics] = useState<Epic[]>(initialEpics);
  const [selectedInitiatives, setSelectedInitiatives] = useState(initialInitiatives);
  const [selectedOutcomes, setSelectedOutcomes] = useState(initialOutcomes);

  const form = useForm<Partial<any>>({
    initialValues,
    validate: {
      name: isNotEmpty(),
      description: isNotEmpty(),
      start_date: (value, values) => {
        if (value && values.end_date && new Date(values.end_date) < new Date(value)) {
          return i18n.t('strategy.initiatives.validation.start_date_before_end_date');
        }
      },
      end_date: (value, values) => {
        if (dayjs(value).endOf('day').isBefore(dayjs())) {
          return i18n.t('strategy.initiatives.validation.end_date_future');
        }
        if (value && values.start_date && new Date(value) < new Date(values.start_date)) {
          return i18n.t('strategy.initiatives.validation.end_date_after_start_date');
        }
      },
    },
  });

  useDeepCompareEffect(() => {
    const currentValues = form.values;
    const hasChanges = !isEqual(currentValues, initialValues);

    if (hasChanges) {
      form.setValues(initialValues);
      form.resetDirty();
    }
  }, [initialValues]);

  useDeepCompareEffect(() => {
    const hasChanges = !isEqual(initialEpics, selectedEpics);

    if (hasChanges) {
      setSelectedEpics(initialEpics);
    }
  }, [initialEpics]);

  useDeepCompareEffect(() => {
    const hasChanges = !isEqual(initialInitiatives, selectedInitiatives);

    if (hasChanges) {
      setSelectedInitiatives(initialInitiatives);
    }
  }, [initialInitiatives]);

  useDeepCompareEffect(() => {
    const hasChanges = !isEqual(initialOutcomes, selectedOutcomes);

    if (hasChanges) {
      setSelectedOutcomes(initialOutcomes);
    }
  }, [initialOutcomes]);

  const resetModalState = () => {
    setStep(1);
    setFormData({});
    form.reset();
    setSelectedEpics(initialEpics);
    setSelectedInitiatives(initialInitiatives);
    setSelectedOutcomes(initialOutcomes);
  };

  const handleClose = () => {
    const userConfirmed = window.confirm(i18n.t('strategy.initiatives.unsaved_changes'));

    if (userConfirmed) {
      onClose();
      setTimeout(resetModalState, 300);
    }
  };

  const handleStepOneNext = (data: Partial<any>) => {
    setFormData({ ...formData, ...data });
    setStep(2);
  };

  const handleStepTwoBack = () => {
    setStep(1);
  };

  const handleStepTwoSubmit = async () => {
    if (entityType === 'initiative' && !portfolioId) {
      return;
    }

    if ((entityType === 'outcome' || entityType === 'goal') && (!organizationId || !portfolioId)) {
      return;
    }

    try {
      // Prepare the submission data based on entity type
      let submitData: any = { ...formData };

      if (entityType === 'initiative') {
        submitData = {
          ...formData,
          portfolios: [portfolioId],
          projects: selectedEpics.flatMap((e: Epic) => e.projects.map((p: any) => p.id)),
          epics: selectedEpics.map((e: Epic) => e.id),
        };
      } else if (entityType === 'outcome') {
        submitData = {
          ...formData,
          initiatives: selectedInitiatives.map((i: any) => i.id),
          organization_id: organizationId,
          portfolio_id: portfolioId,
        };
      } else if (entityType === 'goal') {
        submitData = {
          ...formData,
          outcomes: selectedOutcomes.map((o: any) => o.id),
          initiatives: selectedInitiatives.map((i: any) => i.id),
          organization_id: organizationId,
          portfolio_id: portfolioId,
        };
      }

      await onSubmit(submitData);

      resetModalState();

      if (mode === 'edit' && entityType === 'initiative') {
        setSelectedInitiative({ ...selectedInitiative, ...formData } as any);
      }
    } catch (error) {
      // Error handling is done in the parent component's onError callback
      console.error('Error submitting strategy entity:', error);
    }
  };

  const getTitle = () => {
    const entityName =
      entityType === 'initiative'
        ? i18n.t('common.initiative')
        : entityType === 'outcome'
          ? i18n.t('common.outcome')
          : i18n.t('common.strategic_goal');
    const action = mode === 'create' ? i18n.t('common.actions.create') : i18n.t('common.actions.edit');
    return `${action} ${entityName}`;
  };

  const title = (
    <Text size="large" weight="bold">
      {getTitle()} <span style={{ color: skyBase }}>{i18n.t('strategy.initiatives.step_indicator', { step })}</span>
    </Text>
  );

  return (
    <StyledModal
      data-bf="strategy-modal-entity"
      size={580}
      opened={opened}
      onClose={handleClose}
      title={title}
      centered
    >
      {step === 1 && (
        <StepOne
          mode={mode}
          entityType={entityType}
          form={form}
          onNext={handleStepOneNext}
          onCancel={handleClose}
          onDelete={onDelete}
          isDeleting={isDeleting}
        />
      )}
      {step === 2 && (
        <StepTwo
          mode={mode}
          entityType={entityType}
          selectedEpics={selectedEpics}
          setSelectedEpics={setSelectedEpics}
          selectedInitiatives={selectedInitiatives}
          setSelectedInitiatives={setSelectedInitiatives}
          selectedOutcomes={selectedOutcomes}
          setSelectedOutcomes={setSelectedOutcomes}
          onBack={handleStepTwoBack}
          onCancel={handleClose}
          onSubmit={handleStepTwoSubmit}
          isSubmitting={isSubmitting}
        />
      )}
    </StyledModal>
  );
}

export const StyledModal = styled(Modal)`
  .mantine-Modal-content {
    box-sizing: border-box;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    border-radius: 8px;
    min-width: 580px;
  }

  .mantine-Modal-header {
    padding: 0;
    min-height: 36px;
  }

  .mantine-Modal-close {
    position: absolute;
    right: 0px;
    top: 0px;
    color: var(--sky-dark);
    background-color: transparent;
  }

  .mantine-Modal-body {
    padding: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
`;
